/**
 * Sub-modal pieces for InvoiceFormModals.
 */

import type { Address } from '@/gen/proto'

import { Modal } from '../../ui'
import { AddressCard } from '../AddressCard'

import type { UseInvoiceFormReturn } from './useInvoiceForm'

export function getShippingModalTitle(invoiceType: string, isVendor: boolean): string {
  if (invoiceType === 'export') { return 'Select Consignee Address' }
  if (isVendor) { return 'Select Delivery Address' }
  return 'Select Shipping Address'
}

export function AddNewAddressButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button
      className="w-full p-4 border-2 border-dashed border-border rounded-lg hover:border-primary hover:bg-muted transition-colors text-muted-foreground hover:text-primary flex items-center justify-center gap-2"
      type="button"
      onClick={onClick}
    >
      <span className="text-xl">+</span>
      <span className="text-sm font-medium">Add New Address</span>
    </button>
  )
}

interface BillingSelectorProps {
  isOpen: boolean
  isVendor: boolean
  allPartyAddresses: Address[]
  selectedShipToAddress: Address | null
  onClose: () => void
  onSelect: (address: Address) => void
  onAddNew: () => void
}

export function BillingAddressSelectorModal(p: BillingSelectorProps): JSX.Element {
  return (
    <Modal
      isOpen={p.isOpen}
      size="md"
      title={p.isVendor ? 'Select Supplier Address' : 'Select Billing Address'}
      onClose={p.onClose}
    >
      <div className="space-y-3">
        {p.allPartyAddresses.map((address) => (
          <button
            key={address.id}
            className="w-full text-left"
            type="button"
            onClick={() => p.onSelect(address)}
          >
            <AddressCard
              address={address}
              selected={p.selectedShipToAddress?.id === address.id}
              showActions={false}
              onSelect={() => {}}
            />
          </button>
        ))}
        <AddNewAddressButton onClick={p.onAddNew} />
      </div>
    </Modal>
  )
}

interface ShippingSelectorProps {
  isOpen: boolean
  isVendor: boolean
  invoiceType: string
  shippingAddressOptions: Address[]
  selectedShipToAddress: Address | null
  selectedConsigneeAddress: Address | null
  consigneeType: string
  onClose: () => void
  onSelectSame: () => void
  onSelectParty: (address: Address) => void
  onAddNew: () => void
}

export function ShippingAddressSelectorModal(p: ShippingSelectorProps): JSX.Element {
  return (
    <Modal
      isOpen={p.isOpen}
      size="md"
      title={getShippingModalTitle(p.invoiceType, p.isVendor)}
      onClose={p.onClose}
    >
      <div className="space-y-3">
        {!p.isVendor && p.selectedShipToAddress && (
          <div>
            <p className="text-xs font-semibold text-foreground-secondary uppercase tracking-wide mb-1">
              {p.invoiceType === 'export' ? 'Same as Client Address' : 'Same as Billing Address'}
            </p>
            <button className="w-full text-left" type="button" onClick={p.onSelectSame}>
              <AddressCard
                address={p.selectedShipToAddress}
                selected={p.consigneeType === 'same'}
                showActions={false}
                onSelect={() => {}}
              />
            </button>
          </div>
        )}
        {p.shippingAddressOptions
          .filter((a) => a.id !== p.selectedShipToAddress?.id)
          .map((address) => (
            <button
              key={address.id}
              className="w-full text-left"
              type="button"
              onClick={() => p.onSelectParty(address)}
            >
              <AddressCard
                address={address}
                selected={p.selectedConsigneeAddress?.id === address.id}
                showActions={false}
                onSelect={() => {}}
              />
            </button>
          ))}
        <AddNewAddressButton onClick={p.onAddNew} />
      </div>
    </Modal>
  )
}

interface ValidationErrorModalProps {
  isOpen: boolean
  errors: Record<string, string>
  onClose: () => void
}

const ERROR_FIELD_LABELS: Record<string, string> = {
  party: 'Customer',
  invoiceDate: 'Invoice Date',
  items: 'Line Items',
  lutNumber: 'LUT Number',
  exchangeRate: 'Exchange Rate',
  countryOfDestination: 'Country of Destination',
  shippingAddress: 'Shipping Address',
  changeNotes: 'Reason for Edit',
}

export function ValidationErrorModal({ isOpen, errors, onClose }: ValidationErrorModalProps): JSX.Element {
  return (
    <Modal isOpen={isOpen} size="sm" title="Validation Errors" onClose={onClose}>
      <div className="text-foreground">
        <p className="mb-2 text-sm text-foreground-secondary">
          Please fix the following errors before submitting:
        </p>
        <ul className="list-none p-0 m-0">
          {Object.entries(errors).map(([field, message]) => (
            <li
              key={field}
              className="py-1.5 px-2 mb-1 bg-red-50 dark:bg-red-900/20 border-l-[3px] border-red-600 rounded-sm text-xs text-red-800 dark:text-red-200"
            >
              <strong className="text-red-900 dark:text-red-100">
                {ERROR_FIELD_LABELS[field] ?? field}:
              </strong>{' '}
              {message}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  )
}

interface SelectedAddressApplier {
  type: 'billing' | 'shipping' | 'consignee' | 'notify_party'
  form: UseInvoiceFormReturn
  address: Address
}

function applySelectedAddress({ type, form, address }: SelectedAddressApplier): void {
  if (type === 'billing' || type === 'shipping') {
    form.setSelectedShipToAddress(address)
    form.setShipToType('same')
    return
  }
  if (type === 'consignee') {
    form.setSelectedConsigneeAddress(address)
    form.setConsigneeType('same')
    return
  }
  if (type === 'notify_party') {
    form.setSelectedNotifyPartyAddress(address)
    form.setNotifyPartyType('same')
  }
}

export async function handleAddAddress(
  address: Address,
  type: 'billing' | 'shipping' | 'consignee' | 'notify_party',
  form: UseInvoiceFormReturn,
  onPartyUpdate?: () => void,
  orgId?: string,
): Promise<void> {
  const { selectedParty, setErrors } = form
  if (!selectedParty) { return }

  const { addPartyAddress } = await import('../../../services/party-service')
  const sellerOrgId = orgId ?? ''

  try {
    const response = await addPartyAddress(
      { orgId: sellerOrgId, partyId: selectedParty.id, party: selectedParty },
      { ...address, type },
    )

    if (!response.data) { return }
    onPartyUpdate?.()
    const newAddress = type === 'billing' ? response.data.billingAddress : response.data.shippingAddress
    if (newAddress) {
      applySelectedAddress({ type, form, address: newAddress })
    }
  } catch {
    setErrors((prev) => ({ ...prev, address: 'Failed to add address. Please try again.' }))
  }
}
